import { useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Briefcase,
  Building2,
  Check,
  ChevronsUpDown,
  DollarSign,
  DoorOpen,
  Home,
  LayoutGrid,
  Lock,
  LogOut,
  MessagesSquare,
  Plug,
  Plus,
  Settings,
  ShieldCheck,
  UserRound,
  BookOpen,
  Users,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ErrorState,
  LoadingState,
  TopNavLayout,
  ThemeToggle,
  type SidebarNavItem,
} from '@buildbee/ui';
import { BUSINESS_TYPE_LABELS, ROLE_LABELS, type Action, type Role } from '@buildbee/contracts';
import { can, type ResourceRef } from '@buildbee/domain';
import { authClient } from '../lib/auth-client';
import { withFeatures, type Feature } from '../lib/features';
import { trpc } from '../lib/trpc';
import { setWorkspaceSlug, useWorkspaceSlug } from '../lib/workspace';
import { PlanBanner, UpgradeRequired, planFeatureForPathname } from '../modules/workspace/settings/billing';
import { CompanyMark } from './brand';

type NavDef = {
  key: string;
  label: string;
  to: string;
  icon: ReactNode;
  match: (path: string) => boolean;
  /** Shown when the role may do any of these (a hint only: the API enforces every call). */
  requires?: [Action, ResourceRef][];
  /** Release flag (lib/features.ts): hidden while the flag is off. */
  feature?: Feature;
};

const NAV: NavDef[] = [
  { key: 'home', label: 'Home', to: '/', icon: <Home />, match: (p) => p === '/' },
  {
    key: 'crm',
    label: 'CRM',
    to: '/crm',
    icon: <Users />,
    match: (p) => p.startsWith('/crm'),
    requires: [
      ['view', 'contact'],
      ['view', 'company'],
    ],
  },
  {
    key: 'projects',
    label: 'Projects',
    to: '/projects',
    icon: <Briefcase />,
    match: (p) => p.startsWith('/projects'),
    requires: [['view', 'project']],
  },
  {
    key: 'financials',
    label: 'Financials',
    to: '/financials',
    icon: <DollarSign />,
    match: (p) => p.startsWith('/financials'),
    requires: [
      ['view', 'budget'],
      ['view', 'invoice'],
      ['view', 'lien_waiver'],
    ],
  },
  {
    key: 'portal',
    label: 'Client portal',
    to: '/portal/admin',
    icon: <MessagesSquare />,
    match: (p) => p.startsWith('/portal/admin'),
    requires: [['view', 'portal']],
  },
  {
    key: 'integrations',
    feature: 'integrations',
    label: 'Integrations',
    to: '/integrations',
    icon: <Plug />,
    match: (p) => p.startsWith('/integrations'),
    requires: [
      ['view', 'email'],
      ['manage', 'settings'],
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    to: '/settings',
    icon: <Settings />,
    match: (p) => p.startsWith('/settings'),
  },
];

/** Signed-in shell: resolves the active workspace, then renders sidebar + page. */
export function AppLayout() {
  const me = trpc.workspace.me.get.useQuery();
  const slug = useWorkspaceSlug();
  const navigate = useNavigate();

  const workspaces = me.data?.workspaces;
  // A client (portal access only, no team membership) goes to their portal, not to onboarding.
  const portals = trpc.portal.auth.myPortals.useQuery(undefined, { enabled: workspaces?.length === 0 });
  useEffect(() => {
    if (!workspaces) return;
    if (workspaces.length === 0) {
      if (portals.isPending) return;
      void navigate({ to: portals.data?.length ? '/portal' : '/onboarding', replace: true });
      return;
    }
    if (!slug || !workspaces.some((w) => w.slug === slug)) {
      const preferred = workspaces.find((w) => w.id === me.data?.lastWorkspaceId) ?? workspaces[0]!;
      setWorkspaceSlug(preferred.slug);
    }
  }, [workspaces, portals.isPending, portals.data, slug, me.data?.lastWorkspaceId, navigate]);

  if (me.isLoading) return <LoadingState label="Loading your workspace" className="min-h-dvh" />;
  if (me.error) return <ErrorState className="m-8" error={me.error} onRetry={() => void me.refetch()} />;
  const active = workspaces?.find((w) => w.slug === slug);
  if (!active) return <LoadingState label="Opening workspace" className="min-h-dvh" />;

  return <Shell key={active.id} />;
}

function Shell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = trpc.workspace.current.useQuery();
  const role = current.data?.membership.role as Role | undefined;
  const custom = !!current.data?.membership.permissionTemplateId;
  const platform = trpc.platform.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const unread = trpc.portal.team.messages.unreadCount.useQuery(undefined, {
    enabled: !!role && can({ userId: '', role }, 'view', 'portal'),
    refetchInterval: 60_000,
  });
  const visible = (item: NavDef) =>
    !item.requires ||
    !role ||
    custom ||
    item.requires.some(([action, resource]) => can({ userId: '', role }, action, resource));
  // Plan gate: the API refuses these calls anyway; show an upgrade page instead of a page full of errors.
  const entitlements = current.data?.workspace.entitlements;
  // Hive-managed workspaces upgrade in Hive and get an "All apps" link back to Hive's launcher.
  const hiveLinks = current.data?.workspace.billingMode === 'hive' ? current.data.workspace.hiveLinks : null;
  const billingContext = current.data
    ? { billingMode: current.data.workspace.billingMode, billingUrl: hiveLinks?.billingUrl ?? null }
    : undefined;
  const routeFeature = planFeatureForPathname(pathname);
  const lockedFeature = routeFeature && entitlements?.active && !entitlements.features.includes(routeFeature) ? routeFeature : null;
  const nav: SidebarNavItem[] = withFeatures(NAV).filter(visible).map((item) => ({
    key: item.key,
    label: item.label,
    icon: item.icon,
    active: item.match(pathname),
    badge:
      item.key === 'portal' && (unread.data?.unread ?? 0) > 0
        ? <span className="min-w-5 rounded-full bg-primary px-1.5 text-center text-[11px] font-semibold leading-5 text-primary-foreground">{unread.data!.unread}</span>
        : item.key === 'financials' && entitlements && !entitlements.features.includes('financials') && entitlements.active
          ? <Lock className="size-3.5 text-muted-foreground" aria-label="Premium" />
          : undefined,
    render: ({ className, children, onNavigate }) => (
      <Link to={item.to} className={className} onClick={onNavigate}>
        {children}
      </Link>
    ),
  }));
  // Platform console link: only for BuildBee super admins (the API enforces this too).
  if (platform.data?.platformRole === 'super_admin') {
    nav.push({
      key: 'platform',
      label: 'Platform admin',
      icon: <ShieldCheck />,
      active: pathname.startsWith('/admin'),
      render: ({ className, children, onNavigate }) => (
        <Link to="/admin" className={className} onClick={onNavigate}>
          {children}
        </Link>
      ),
    });
  }

  return (
    <TopNavLayout
      brand={<WorkspaceSwitcher />}
      nav={nav}
      actions={
        <>
          {hiveLinks?.appsUrl && (
            <a
              href={hiveLinks.appsUrl}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
              title="All your WRKRBZ apps in Hive"
            >
              <LayoutGrid className="size-4" aria-hidden />
              All apps
            </a>
          )}
          <ThemeToggle />
          <UserMenu />
        </>
      }
    >
      {entitlements && <PlanBanner entitlements={entitlements} billingContext={billingContext} />}
      {lockedFeature ? <UpgradeRequired feature={lockedFeature} billingContext={billingContext} /> : <Outlet />}
    </TopNavLayout>
  );
}

function WorkspaceSwitcher() {
  const me = trpc.workspace.me.get.useQuery();
  const slug = useWorkspaceSlug();
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const setLast = trpc.workspace.me.setLastWorkspace.useMutation();
  const navigate = useNavigate();
  const current = me.data?.workspaces.find((w) => w.slug === slug);
  // Companies where this person is also a client (rare): their portals are listed below the
  // workspaces. Hidden when there are none, which is almost everyone.
  const portals = trpc.portal.auth.myPortals.useQuery(undefined, { staleTime: 5 * 60_000, retry: false });
  const clientOf = (portals.data ?? []).filter((p) => !me.data?.workspaces.some((w) => w.slug === p.slug));
  // The company's uploaded logo (signed URL, valid 1 h) shows beside the WRKRBZ mark.
  const workspace = trpc.workspace.current.useQuery(undefined, { enabled: Boolean(slug), staleTime: 5 * 60_000 });

  const switchTo = async (w: { id: string; slug: string }) => {
    if (w.slug === slug) return;
    setWorkspaceSlug(w.slug);
    setLast.mutate({ workspaceId: w.id });
    // Everything cached belongs to the previous workspace.
    queryClient.removeQueries({ predicate: (q) => !JSON.stringify(q.queryKey).includes('"me"') });
    await utils.invalidate();
    await navigate({ to: '/' });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex max-w-60 items-center gap-2.5 rounded-lg py-1.5 pl-1 pr-2 text-left transition-colors hover:bg-foreground/[0.04]"
          title={current ? BUSINESS_TYPE_LABELS[current.businessType] : undefined}
        >
          <CompanyMark brand={{ name: current?.name ?? 'BuildBee', logoUrl: workspace.data?.workspace.logoUrl ?? null }} size="sm" />
          <span className="min-w-0 truncate text-[15px] font-semibold">{current?.name ?? 'BuildBee'}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        {me.data?.workspaces.map((w) => (
          <DropdownMenuItem key={w.id} icon={<Building2 />} onSelect={() => void switchTo(w)}>
            <span className="flex-1 truncate">{w.name}</span>
            <span className="text-xs text-muted-foreground">{ROLE_LABELS[w.role]}</span>
            {w.slug === slug ? <Check className="!text-accent-foreground" /> : null}
          </DropdownMenuItem>
        ))}
        {clientOf.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Client portals</DropdownMenuLabel>
            {clientOf.map((p) => (
              <DropdownMenuItem key={p.slug} icon={<DoorOpen />} onSelect={() => void navigate({ to: '/portal/$slug', params: { slug: p.slug } })}>
                <span className="flex-1 truncate">{p.name}</span>
                <span className="text-xs text-muted-foreground">Client</span>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        <DropdownMenuSeparator />
        {/* One company per owner: BuildBee support adds more (each on its own plan). */}
        {me.data?.canCreateWorkspace ? (
          <DropdownMenuItem icon={<Plus />} onSelect={() => void navigate({ to: '/onboarding' })}>
            Create workspace
          </DropdownMenuItem>
        ) : (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">Need another company? Contact BuildBee support.</p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const me = trpc.workspace.me.get.useQuery();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = me.data?.user;
  if (!user) return null;

  const signOut = async () => {
    await authClient.signOut();
    setWorkspaceSlug(null);
    queryClient.clear();
    await navigate({ to: '/sign-in' });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`Account: ${user.name}`} className="rounded-full transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30">
          <Avatar name={user.name} src={user.image} size="md" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="normal-case">
          <span className="block truncate text-[14px] font-semibold text-foreground">{user.name}</span>
          <span className="block truncate font-normal">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<UserRound />} onSelect={() => void navigate({ to: '/settings/profile' })}>
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem icon={<BookOpen />} onSelect={() => void navigate({ to: '/help' })}>
          User guide
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<LogOut />} onSelect={() => void signOut()}>
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
